/** Global search across the entity types. */

import { useQuery } from '@tanstack/react-query';
import { graphql } from '../gql';
import { request } from '../lib/graphql';
import { useLocation } from '../lib/router';
import { ErrorState, Link, Loading, PageHead, SectionHead } from '../components/ui';
import { money, truncate } from '../lib/format';

const SEARCH = graphql(`
  query GlobalSearch($q: String!, $id: ID!) {
    companies(where: { OR: [{ name_CONTAINS: $q }, { uic_CONTAINS: $id }] }, options: { limit: 12 }) {
      uic name legal_form
    }
    people(where: { name_CONTAINS: $q }, options: { limit: 12 }) { person_hash name }
    municipalities(where: { name_CONTAINS: $q }, options: { limit: 12 }) { key name province }
    tenders(
      where: { OR: [{ title_CONTAINS: $q }, { tender_id_CONTAINS: $id }] }
      options: { limit: 12 }
    ) { tender_id title awarded_amount currency }
    parties(where: { name_CONTAINS: $q }, options: { limit: 8 }) { key name }
  }
`);

export function SearchPage() {
  const { location } = useLocation();
  const term = location.params.get('q') ?? '';

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['search', term],
    queryFn: () => request(SEARCH, { q: term, id: term }),
    enabled: term.length > 1,
  });

  if (term.length < 2) {
    return <PageHead eyebrow="Търсене" title="Търсене" lede="Въведете име или ЕИК." />;
  }
  if (isPending) return <Loading />;
  if (isError) return <ErrorState message={(error).message} />;

  const groups: [string, { href: string; label: string; note?: string }[]][] = [
    ['Фирми', data.companies.map((row) => ({
      href: `#/companies/${row.uic}`,
      label: row.name ?? row.uic,
      note: [row.uic, row.legal_form].filter(Boolean).join(' · '),
    }))],
    ['Лица', data.people.map((row) => ({
      href: `#/people/${row.person_hash}`,
      label: row.name ?? 'Без име',
    }))],
    ['Общини', data.municipalities.map((row) => ({
      href: `#/municipalities/${encodeURIComponent(row.key)}`,
      label: row.name ?? row.key,
      note: row.province ?? undefined,
    }))],
    ['Поръчки', data.tenders.map((row) => ({
      href: `#/tenders/${encodeURIComponent(row.tender_id)}`,
      label: truncate(row.title ?? row.tender_id, 70),
      note: money(row.awarded_amount, row.currency),
    }))],
    ['Партии', data.parties.map((row) => ({
      href: `#/parties/${encodeURIComponent(row.key)}`,
      label: row.name ?? row.key,
    }))],
  ];

  const total = groups.reduce((sum, [, rows]) => sum + rows.length, 0);

  return (
    <>
      <PageHead eyebrow="Търсене" title={`„${term}“`}
        lede={total ? `${total} съвпадения` : 'Няма съвпадения.'} />
      {groups.filter(([, rows]) => rows.length > 0).map(([title, rows]) => (
        <section className="block" key={title}>
          <SectionHead title={title} aside={String(rows.length)} />
          <ul className="search-hits">
            {rows.map((row) => (
              <li key={row.href}>
                <Link href={row.href}>{row.label}</Link>
                {row.note && <span className="uic mono">{row.note}</span>}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}
